import { FC, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import { appSchemaResolver } from "shared/lib/helpers/form";
import { Button } from "shared/ui/shadcn/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "shared/ui/shadcn/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "shared/ui/shadcn/form";
import { Input } from "shared/ui/shadcn/input";
import { RadioGroup, RadioGroupItem } from "shared/ui/shadcn/radio-group";
import { Textarea } from "shared/ui/shadcn/textarea";

import {
  BUG_REPORT_SEVERITIES,
  BugReportSeverityT,
  CreateBugReportResponseI,
} from "@/api/routes/bug-reports";
import { useCreateBugReport } from "@/modules/bug-reports/data/queries";
import {
  BUG_REPORT_FORM_DEFAULTS,
  BugReportContextI,
  BugReportFormValuesT,
  bugReportFormSchema,
  buildBugReportRequest,
  describeBugReportFailure,
} from "@/modules/bug-reports/lib/form";

const SEVERITY_LABELS: Record<BugReportSeverityT, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
};

interface BugReportDialogPropsI {
  /** Where the caller was. The app fills it, not the caller. */
  context: BugReportContextI;
  onClose: () => void;
}

/**
 * The Submit bug form (GLE-8483): a title, a severity, a description and an
 * optional Loom link. On success it shows the Linear issue ID.
 */
export const BugReportDialog: FC<BugReportDialogPropsI> = ({
  context,
  onClose,
}) => {
  const { mutateAsync: createBugReport, isPending } = useCreateBugReport();
  const [failure, setFailure] = useState<string | null>(null);
  const [filed, setFiled] = useState<CreateBugReportResponseI | null>(null);
  const form = useForm<BugReportFormValuesT>({
    resolver: appSchemaResolver(bugReportFormSchema),
    defaultValues: BUG_REPORT_FORM_DEFAULTS,
  });

  const handleSubmit: SubmitHandler<BugReportFormValuesT> = async (values) => {
    setFailure(null);

    try {
      setFiled(await createBugReport(buildBugReportRequest(values, context)));
    } catch (error) {
      setFailure(describeBugReportFailure(error));
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !isPending) {
          onClose();
        }
      }}
    >
      <DialogContent className="max-h-[85vh] max-w-[560px] overflow-y-auto rounded-[20px]">
        <DialogHeader>
          <DialogTitle className="typography-header-8-bold">
            Submit a bug
          </DialogTitle>
          <DialogDescription className="text-black/70 typography-body-4">
            Tell us what went wrong. We add the page you are on.
          </DialogDescription>
        </DialogHeader>

        {filed ? (
          <div className="flex flex-col gap-4">
            <output className="block typography-body-4">
              Thanks. We filed your report as{" "}
              <span className="typography-body-4-semibold">
                {filed.issue_identifier}
              </span>
              .
            </output>
            <DialogFooter>
              <Button type="button" onClick={onClose}>
                Done
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <Form {...form}>
            <form
              className="flex flex-col gap-5"
              noValidate
              onSubmit={form.handleSubmit(handleSubmit)}
            >
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="The dialer froze after a call"
                        disabled={isPending}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="severity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Severity</FormLabel>
                    <FormControl>
                      <RadioGroup
                        className="flex gap-5"
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={isPending}
                      >
                        {BUG_REPORT_SEVERITIES.map((severity) => (
                          <label
                            key={severity}
                            className="flex cursor-pointer items-center gap-2 typography-body-4"
                          >
                            <RadioGroupItem value={severity} />
                            {SEVERITY_LABELS[severity]}
                          </label>
                        ))}
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea
                        rows={5}
                        placeholder="What did you do, what did you expect, and what happened?"
                        disabled={isPending}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="loom_url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Loom link (optional)</FormLabel>
                    <FormControl>
                      <Input
                        type="url"
                        placeholder="https://www.loom.com/share/..."
                        disabled={isPending}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {failure && (
                <p role="alert" className="text-[#E9452E] typography-body-4">
                  {failure}
                </p>
              )}

              <DialogFooter className="gap-2">
                <Button
                  type="button"
                  variant="outline"
                  disabled={isPending}
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isPending}>
                  {isPending ? "Sending…" : "Submit bug"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};
